import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Wordmark } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function AuthPage() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      toast.success("Welcome to BiblePlaza");
      nav("/plaza");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Please try again");
    } finally {
      setLoading(false);
    }
  };

  const continueWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/plaza";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="bp-noise flex min-h-screen items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <button onClick={() => nav("/")} className="mb-8 flex justify-center" data-testid="auth-logo">
          <Wordmark />
        </button>
        <div className="rounded-3xl border border-[#E8E4DF] bg-white p-7 bp-card-shadow bp-fade-up">
          <h1 className="font-display text-2xl font-semibold tracking-tight text-[#0F3D66]">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h1>
          <p className="mt-1 text-sm text-[#6B6763]">
            {mode === "login" ? "Sign in to find and join studies." : "Free to browse and join hosted groups."}
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name" className="text-[#0F3D66]">Name</Label>
                <Input id="name" data-testid="auth-name-input" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 rounded-xl border-[#E8E4DF] focus:border-[#F36353] focus-visible:ring-[#F36353]/20" placeholder="Jane Doe" />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-[#0F3D66]">Email</Label>
              <Input id="email" type="email" data-testid="auth-email-input" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 rounded-xl border-[#E8E4DF] focus:border-[#F36353] focus-visible:ring-[#F36353]/20" placeholder="you@email.com" />
            </div>
            <div>
              <Label htmlFor="password" className="text-[#0F3D66]">Password</Label>
              <Input id="password" type="password" data-testid="auth-password-input" required value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1 rounded-xl border-[#E8E4DF] focus:border-[#F36353] focus-visible:ring-[#F36353]/20" placeholder="••••••••" />
            </div>
            <Button type="submit" disabled={loading} data-testid="auth-submit-btn"
              className="h-11 w-full rounded-full bg-[#F36353] text-white hover:bg-[#D94A3D]">
              {loading ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          {mode === "login" && (
            <p className="mt-3 text-center text-sm">
              <button type="button" data-testid="auth-forgot-link" onClick={() => nav("/forgot-password")}
                className="font-medium text-[#6B6763] hover:text-[#F36353] hover:underline">
                Forgot password?
              </button>
            </p>
          )}

          <div className="my-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-[#E8E4DF]" />
            <span className="text-xs uppercase tracking-wide text-[#A8A39D]">or</span>
            <div className="h-px flex-1 bg-[#E8E4DF]" />
          </div>

          <Button type="button" onClick={continueWithGoogle} data-testid="auth-google-btn"
            className="h-11 w-full rounded-full border border-[#E8E4DF] bg-white text-[#0F3D66] hover:bg-[#F6F3EF]">
            <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
              <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.46 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z" />
            </svg>
            Continue with Google
          </Button>

          <p className="mt-5 text-center text-sm text-[#6B6763]">
            {mode === "login" ? "New here?" : "Already have an account?"}{" "}
            <button
              data-testid="auth-toggle-mode"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="font-semibold text-[#F36353] hover:underline"
            >
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
